import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import useContentful from "./hooks/useContentful";
import { ContentfulNavigationItem, ContentfulNavigationRow } from "./lib/contentfulTypes";
import MobileMenu from "./components/MobileMenu";
import BurgerButton from "./components/BurgerButton";
import DesktopMenu from "./components/DesktopMenu";
import AnnouncementBar, { AnnouncementBarEntry } from "./components/AnnouncementBar/AnnouncementBar";

const targetElementId = 'contentful-header-render-target';

const ContentfulHeader = () => {
  const renderTarget = document.getElementById(targetElementId);
  if (!renderTarget) return null;

  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const entryId = renderTarget?.dataset.entryId;
  const locale = renderTarget?.dataset.shopLocale;

  const brandDiv = document.getElementById('site-brand-setting');
  const brand = brandDiv?.dataset?.brand || 'fender';

  const marketMetafieldsEl = document.getElementById('react-market-metafields');
  const eCommerceDisabled = marketMetafieldsEl?.dataset.disableEcommerce || false;
  const marketHandle = marketMetafieldsEl?.dataset.handle || '';

  const {contentfulData} = useContentful({'sys.id': entryId, include: 10, locale});
  const fields = contentfulData?.items?.[0]?.fields || {};

  const announcementBar = fields.announcementBar as AnnouncementBarEntry | undefined;

  const rawMenuItems = Array.isArray(fields.navigation)
    ? (fields.navigation as ContentfulNavigationItem[])
    : [];
  const localizationFilter = (item: ContentfulNavigationItem|ContentfulNavigationRow) => {
    if (
      item.fields.eCommerceDisplay &&
      (
        item.fields.eCommerceDisplay == 'Display in e-commerce enabled markets only' && eCommerceDisabled
        || item.fields.eCommerceDisplay == 'Display in non e-commerce markets only' && !eCommerceDisabled
      )
    ) {
      return false;
    }

    if (item.fields.marketInclusion && item.fields.marketInclusion.length > 0) {
      if (!item.fields.marketInclusion.includes(marketHandle)) {
        return false;
      }
    }

    return true;
  };

  let menuItems = rawMenuItems.filter(localizationFilter).map(topLevelItem => {
    if (topLevelItem.fields.subItems) {
      const tier2Items = topLevelItem.fields.subItems.map(tier2Item => {
        if (tier2Item.fields.subItems) {
          const filteredTier3Array = tier2Item.fields.subItems.filter(localizationFilter);
          return {...tier2Item, fields: {...tier2Item.fields, subItems: filteredTier3Array}};
        }

        return tier2Item;
      });
      // @ts-ignore Filter the second tier, now that the third tier has been filtered.
      const filteredTier2Items = tier2Items.filter(localizationFilter);
      return {...topLevelItem, fields: {...topLevelItem.fields, subItems: filteredTier2Items}};
    }

    return topLevelItem;
  });

  const ghostNavEnabled = document.documentElement.getAttribute('data-ghost-nav-enabled') == 'true';

  let supportNav: ContentfulNavigationItem[] = [];
  if (brand == 'fender' && Array.isArray(fields.supportNavigation)) {
    supportNav = fields.supportNavigation as ContentfulNavigationItem[];
  }

  const announcementBarTarget = document.getElementById('contentful-announcement-bar-target');
  const mobileMenuTarget = document.getElementById('contentful-mobile-menu-render-target');
  const burgerTarget = document.getElementById('contentful-hamburger-button-target');
  const desktopMenuTarget = document.getElementById('contentful-desktop-menu-target');
  const logoUrl = mobileMenuTarget?.dataset.logoUrl;

  const openMobileMenu = () => {
    setMobileMenuIsOpen(true);
    document.documentElement.style.overflow = 'hidden';
  }

  const closeMobileMenu = () => {
    setMobileMenuIsOpen(false);
    document.documentElement.style.overflow = 'initial';
  }

  const handleResize = () => {
    if (window.innerWidth >= 1150 && mobileMenuIsOpen) {
      closeMobileMenu();
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => { window.removeEventListener('resize', handleResize); }
  });

  return (
    <>
      {announcementBar && announcementBarTarget && createPortal(
        <AnnouncementBar
          id={announcementBar.sys.id}
          brand={brand}
          fields={announcementBar.fields}
          sys={announcementBar.sys}
          metadata={announcementBar.metadata}
          ghostNavEnabled={ghostNavEnabled}
        />,
        announcementBarTarget
      )}

      {menuItems.length > 0 && mobileMenuTarget && createPortal(
        <MobileMenu
          brand={brand}
          isOpen={mobileMenuIsOpen}
          items={menuItems}
          logoUrl={logoUrl}
          onClose={closeMobileMenu}
          supportNav={supportNav}
        />,
        mobileMenuTarget
      )}
      {burgerTarget && createPortal(
        <BurgerButton 
          // Todo: Fender has a transparent menu option, which will need to be considered here.
          fill={(brand == 'jackson') ? '#fff' : '#000'}
          onClick={openMobileMenu} 
        />,
        burgerTarget
      )}
      {menuItems.length > 0 && desktopMenuTarget && createPortal(
        <DesktopMenu brand={brand} items={menuItems} supportNav={supportNav} />,
        desktopMenuTarget
      )}
    </>
  );
}

createRoot(document.getElementById(targetElementId) as HTMLElement).render(<ContentfulHeader />);